import dayjs from 'dayjs';
import { PIPELINE_COLUMNS, queryStatusLabel } from '../../pipeline';
import { businessSendOutcome } from '../../../../features/switchover/models/businessSendOutcome';

// Keep both authoritative streams, including undated entries, in one stable feed.
export function mergeTaskActivity(timeline = [], movements = []) {
  const entry = (resource, type, time, index) => {
    const parsed = time ? Date.parse(time) : NaN;
    return { resource, type, time, index, key: type === 'movement' ? `movement:${resource.id ?? `${time}:${index}`}` : `event:${resource.event}:${time || 'undated'}:${resource.id ?? index}`,
      timestamp: Number.isFinite(parsed) ? parsed : null };
  };
  return [
    ...timeline.map((resource, index) => entry(resource, "event", resource.time, index)),
    ...movements.map((resource, index) => entry(resource, "movement", resource.moved_at, index)),
  ].sort((a, b) => {
    if (a.timestamp !== b.timestamp) return (b.timestamp ?? -Infinity) - (a.timestamp ?? -Infinity);
    if (a.type !== b.type) return a.type === 'movement' ? -1 : 1;
    return a.resource.id != null && b.resource.id != null ? String(a.resource.id).localeCompare(String(b.resource.id), undefined, { numeric: true }) : a.index - b.index;
  });
}

export function personName(user) {
  if (!user) return null;
  if (typeof user === "string") return user;
  return user.display_name || [user.name, user.surname].filter(Boolean).join(" ") || user.fullName || user.username || null;
}

const columns = PIPELINE_COLUMNS.filter(column => !column.isChat);
const stage = status => columns.find(column => column.key === status || column.statuses?.includes(status));
export function movementMeaning(from, to) {
  if (from === to) return 'note';
  const a = stage(from), b = stage(to);
  if (b?.key === 'cancelled') return 'lost';
  if (b?.key === 'parked') return 'parked';
  if (a && ['parked', 'cancelled'].includes(a.key) && b) return 'resumed';
  if (!a || !b) return 'neutral';
  return columns.indexOf(b) > columns.indexOf(a) ? 'forward' : columns.indexOf(b) < columns.indexOf(a) ? 'back' : 'neutral';
}
const money = value => value == null || value === '' || !Number.isFinite(Number(value)) ? null : `AED ${Number(value).toLocaleString('en-US')}`;
const humanise = value => String(value || '').replace(/_v\d+/gi, '').replace(/\bv\d+\b/gi, '').replace(/_/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
const stamp = value => value && dayjs(value).isValid() ? dayjs(value) : null;
export function elapsedLabel(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000), hours = Math.floor(mins / 60), days = Math.floor(hours / 24);
  return days ? `${days}d${hours % 24 ? ` ${hours % 24}h` : ''}` : hours ? `${hours}h${mins % 60 ? ` ${mins % 60}m` : ''}` : `${mins}m`;
}
const actorName = (resource, type, users) => type === 'movement'
  ? personName(resource.moved_by_user) || resource.moved_by_name || personName(users[resource.moved_by])
  : personName(users[resource.user_id]);
const contextFor = (event, parts) => {
  const part = event.query_part_id == null ? null : parts.find(item => String(item.id) === String(event.query_part_id));
  const quotes = part?.supplier_quotes ?? part?.quotes ?? [];
  const quote = quotes.find(item => event.selected_quote_id != null && String(item.id) === String(event.selected_quote_id))
    || quotes.find(item => event.supplier_id != null && String(item.supplier_id) === String(event.supplier_id));
  const supplier = quote?.supplier_name || parts.flatMap(item => item.supplier_quotes ?? item.quotes ?? [])
    .find(item => event.supplier_id != null && String(item.supplier_id) === String(event.supplier_id))?.supplier_name;
  return { part: part?.part_name, supplier, quote };
};
function eventRow(e, parts) {
  if (e.event === 'customer_business_send_failed') {
    const outcome = businessSendOutcome(e);
    return { sentence: outcome?.message || 'Customer message could not be sent',
      details: [e.action && humanise(e.action), outcome?.reason].filter(Boolean),
      meaning: outcome?.delivery === 'rejected' ? 'lost' : 'back' };
  }
  const resolved = contextFor(e, parts);
  const label = { draft_created: 'Draft created', sent_to_suppliers: 'Sent to suppliers', status_changed: 'Status changed',
    unavailable_parts_notified: 'Customer notified of unavailable parts', part_created: 'Part added', images_added: 'Photos added',
    quote_selected: 'Quote selected', quote_updated: 'Quote updated', sent_to_suppliers_v2: 'Sent to suppliers',
    quote_sent_to_customer: 'Quote shared with the customer', customer_quote_accepted: 'Customer accepted the quote',
    part_selling_price_updated_before_quote_share: 'Part selling price updated before quote share', date_bumped: 'Date bumped',
    payments_v2_invoice_unlocked: 'Invoice unlocked', customer_acknowledgement_sent: 'Acknowledgement sent to the customer' }[e.event];
  if (e.event === 'supplier_quote_received') {
    const price = money(e.quoted_price);
    return { sentence: `Quote received${resolved.supplier ? ` from ${resolved.supplier}` : ''}${resolved.part ? ` for ${resolved.part}` : ''}${price ? `: ${price}` : ''}`,
      segments: [{ text: 'Quote received' }, ...(resolved.supplier ? [{ text: ' from ' }, { text: resolved.supplier, strong: true }] : []),
        ...(resolved.part ? [{ text: ' for ' }, { text: resolved.part, strong: true }] : []), ...(price ? [{ text: `: ${price}` }] : [])],
      details: [Number(e.delivery_cost) > 0 && `+ ${money(e.delivery_cost)} delivery`, Number(e.warranty_days) > 0 && `${e.warranty_days}d warranty`].filter(Boolean),
      hint: resolved.quote && money(resolved.quote.quoted_price) && price && Number(resolved.quote.quoted_price) !== Number(e.quoted_price) ? `now ${money(resolved.quote.quoted_price)}` : null,
      meaning: 'forward', entered: true };
  }
  let sentence = label || 'Other activity';
  const details = [label ? (typeof e.details === 'string' ? e.details : null) : humanise(e.event)].filter(Boolean);
  const subject = [resolved.part, resolved.supplier && `(${resolved.supplier})`].filter(Boolean).join(' ');
  const channel = e.communication_channel ? ` on ${String(e.communication_channel).toLowerCase() === 'wati' ? 'WATI' : humanise(e.communication_channel)}` : '';
  switch (e.event) {
    case 'sent_to_suppliers':
    case 'sent_to_suppliers_v2':
      sentence = e.supplier_count != null ? `Sent to ${e.supplier_count} suppliers` : 'Sent to suppliers';
      if (e.attempt != null) sentence += ` (attempt ${e.attempt})`;
      if (Number(e.failed_count) > 0) sentence += ` · ${e.failed_count} failed`;
      if (e.international_order === true || e.international_order === 1) sentence += ' · international';
      break;
    case 'customer_acknowledgement_sent':
      sentence = (e.sent === false || e.sent === 0 ? 'Customer acknowledgement not sent' : 'Acknowledgement sent to the customer') + channel;
      break;
    case 'quote_selected': sentence = subject ? `Quote selected for ${subject}` : 'Quote selected'; break;
    case 'part_selling_price_updated_before_quote_share':
      sentence = `${resolved.part || 'Selling price'} ${[money(e.old_selling_price), money(e.new_selling_price)].filter(Boolean).join(' → ')}`.trim(); break;
    case 'quote_updated': {
      const names = { quoted_price: 'price', delivery_cost: 'delivery', warranty_days: 'warranty' };
      const changes = Array.isArray(e.updates) ? e.updates.map(field => names[field] || humanise(field).toLowerCase()).filter(Boolean) : [];
      sentence = `Quote updated${resolved.supplier || resolved.part ? `: ${[resolved.supplier, resolved.part].filter(Boolean).join(' · ')}` : ''}${changes.length ? ` — ${changes.join(', ')} changed` : ''}`;
      break;
    }
    case 'quote_sent_to_customer': {
      const summary = [Array.isArray(e.lines) && `${e.lines.length} parts`, money(e.grand_total)].filter(Boolean).join(', ');
      sentence = `Quote shared with the customer${summary ? `: ${summary}` : ''}`;
      if (Array.isArray(e.lines)) e.lines.forEach(line => {
        const text = [line.part_name, line.quantity != null && `×${line.quantity}`, line.supplier_name].filter(Boolean).join(' · ');
        if (text) details.push(text);
      });
      break;
    }
    case 'part_created': sentence = `Part added${e.part_name || resolved.part ? `: ${e.part_name || resolved.part}` : ''}${e.quantity != null ? ` ×${e.quantity}` : ''}`; break;
    case 'images_added': sentence = e.count != null ? `${e.count} photos added` : 'Photos added'; break;
    case 'date_bumped': sentence = `Date bumped${stamp(e.previous_created_at) ? ` (was ${dayjs(e.previous_created_at).format('DD MMM')})` : ''}`; break;
    case 'payments_v2_invoice_unlocked': sentence = `Invoice unlocked${e.reason || e.note ? `: ${e.reason || e.note}` : ''}`; if (e.reason && e.note && e.reason !== e.note) details.push(e.note); break;
    case 'customer_quote_accepted': if (e.reason) details.push(e.reason); break;
    default: break;
  }
  return { sentence, details, meaning: 'neutral',
    segments: e.event === 'payments_v2_invoice_unlocked' && (e.reason || e.note) ? [{ text: 'Invoice unlocked: ' }, { text: e.reason || e.note, italic: true }] : undefined,
    stageLine: e.event === 'status_changed' && e.from_status && e.to_status && e.from_status !== e.to_status ? `${queryStatusLabel(e.from_status)} → ${queryStatusLabel(e.to_status)}` : null };
}
const actorIdentity = row => {
  const id = row.type === 'event' ? row.resource.user_id : row.resource.moved_by ?? row.resource.moved_by_user?.id;
  return id != null ? `id:${id}` : row.actor ? `name:${row.actor}` : null;
};
function causesMove(event, movement) {
  const e = event.resource, m = movement.resource;
  switch (e.event) {
    case 'quote_sent_to_customer': return m.to_status === 'waiting_customer_confirmation';
    case 'customer_quote_accepted': return m.to_status === 'confirmed_waiting_payment';
    case 'sent_to_suppliers_v2': return m.to_status === 'waiting_for_price';
    case 'payments_v2_invoice_unlocked': return e.from_status != null && e.to_status != null && e.from_status === m.from_status && e.to_status === m.to_status;
    default: return false;
  }
}
function combineRows(rows, parts) {
  const consumed = new Set();
  const merged = rows.map(row => {
    if (row.type !== 'event' || row.timestamp == null || !actorIdentity(row)) return row;
    const movement = rows.filter(item => item.type === 'movement' && !consumed.has(item.id) && item.timestamp != null
      && actorIdentity(item) === actorIdentity(row) && Math.abs(row.timestamp - item.timestamp) <= 5000 && causesMove(row, item))
      .sort((a, b) => Math.abs(a.timestamp - row.timestamp) - Math.abs(b.timestamp - row.timestamp))[0];
    if (!movement) return row;
    consumed.add(movement.id);
    return { ...row, stageLine: movement.stageLine, meaning: movement.meaning,
      details: [...new Set([...row.details, ...movement.details])], mergedMovementId: movement.id };
  }).filter(row => !consumed.has(row.id));
  const bursts = new Set(['quote_selected', 'part_selling_price_updated_before_quote_share']);
  const result = [];
  for (let i = 0; i < merged.length; i++) {
    const row = merged[i], members = [row];
    if (bursts.has(row.event) && row.timestamp != null && actorIdentity(row)) {
      while (i + 1 < merged.length && bursts.has(merged[i + 1].event) && actorIdentity(merged[i + 1]) === actorIdentity(row)
        && merged[i + 1].timestamp != null && row.timestamp - merged[i + 1].timestamp <= 120000) members.push(merged[++i]);
    }
    if (members.length === 1) { result.push(row); continue; }
    const selected = members.filter(item => item.event === 'quote_selected').map(item => {
      const ctx = contextFor(item.resource, parts);
      return [ctx.part, ctx.supplier && `(${ctx.supplier})`].filter(Boolean).join(' ');
    }).filter(Boolean);
    result.push({ ...row, id: `burst:${members.map(item => item.id).join('|')}`,
      sentence: selected.length ? `Selected quotes: ${selected.join(', ')}` : members.some(item => item.event === 'quote_selected') ? 'Quotes selected' : 'Selling prices updated', details: [],
      expanded: members.map(item => ({ id: item.id, sentence: item.sentence, details: item.details, clock: item.clock })),
      priceChanges: members.filter(item => item.event === 'part_selling_price_updated_before_quote_share').map(item => item.sentence) });
  }
  return result;
}
export function buildActivityFeed({ timeline = [], movements = [], parts = [], userMap = {}, taskStatus, now = Date.now() } = {}) {
  const entries = mergeTaskActivity(timeline, movements);
  const rows = entries.map(entry => {
    const e = entry.resource, actor = actorName(e, entry.type, userMap);
    const common = { id: entry.key,
      timestamp: entry.timestamp, time: entry.time, actor, event: e.event, resource: e, type: entry.type,
      clock: stamp(entry.time)?.format('hh:mm A') || null, fullDate: stamp(entry.time)?.format('DD MMM YYYY, hh:mm A') || null };
    if (entry.type === 'event') return { ...common, ...eventRow(e, parts) };
    const from = personName(e.from_user), to = personName(e.to_user);
    const reassigned = (from || to) && (from !== to || e.from_user_id !== e.to_user_id);
    const meaning = movementMeaning(e.from_status, e.to_status);
    const details = [e.reason, e.remarks !== e.reason && e.remarks].filter(value => typeof value === 'string' && value);
    if (!reassigned && meaning === 'note' && !details.length) return null;
    return { ...common, meaning: reassigned ? 'neutral' : meaning,
      sentence: reassigned ? `Reassigned${from ? ` from ${from}` : ''}${to ? ` to ${to}` : ''}` : meaning === 'note' ? 'Note' : 'Stage changed',
      stageLine: !reassigned && meaning !== 'note' && e.from_status && e.to_status ? `${queryStatusLabel(e.from_status)} → ${queryStatusLabel(e.to_status)}` : null,
      cause: e.direction === 'customer_quote_accepted' ? 'Customer accepted the quote' : null,
      support: e.direction && !['forward', 'backward', 'customer_quote_accepted', 'reassign'].includes(e.direction) ? e.direction : null, details };
  }).filter(Boolean);
  const dated = entries.filter(entry => entry.timestamp != null);
  const previousDates = timeline.filter(e => e.event === 'date_bumped' && stamp(e.previous_created_at)).map(e => dayjs(e.previous_created_at).valueOf());
  const first = dated.length || previousDates.length ? Math.min(...dated.map(entry => entry.timestamp), ...previousDates) : null;
  const ended = ['completed', 'delivered', 'cancelled', 'disabled'].includes(taskStatus);
  const bump = timeline.filter(e => e.event === 'date_bumped' && stamp(e.time)).sort((a, b) => Date.parse(b.time) - Date.parse(a.time))[0];
  const displayRows = combineRows(rows, parts);
  displayRows.forEach((row, index) => {
    const day = stamp(row.time), previous = displayRows[index - 1], older = displayRows[index + 1];
    row.dayKey = day?.format('YYYY-MM-DD') || null;
    row.dayHeader = day && row.dayKey !== previous?.dayKey ? day.format('ddd, DD MMM') : null;
    const gap = row.timestamp != null && older?.timestamp != null ? row.timestamp - older.timestamp : null;
    row.gapAfter = gap >= 600000 ? elapsedLabel(gap) : null;
  });
  return { rows: displayRows, header: first == null ? null : {
    duration: elapsedLabel((ended ? dated[0]?.timestamp : Number(now)) - first), firstDate: dayjs(first).format('DD MMM'),
    ending: ended ? `ended ${queryStatusLabel(taskStatus)}` : 'ongoing',
    bumped: bump ? `date bumped ${dayjs(bump.time).format('DD MMM')}${stamp(bump.previous_created_at) ? ` (was ${dayjs(bump.previous_created_at).format('DD MMM')})` : ''}` : null,
  } };
}
